import { useState, useRef, useCallback } from 'react';
import type { IntakeFormPayload, AssetType, OperatingCondition } from '../types';
import { submitIntake, uploadMedia, triggerAnalysis } from '../lib/api';

// ── Constants ──────────────────────────────────────────────────────────────

const ASSET_TYPES: { value: AssetType; label: string }[] = [
  { value: 'rotating_machinery', label: 'Rotating Machinery' },
  { value: 'electrical_panel', label: 'Electrical Panel' },
  { value: 'hydraulic_system', label: 'Hydraulic System' },
  { value: 'conveyor', label: 'Conveyor' },
  { value: 'pump', label: 'Pump' },
  { value: 'compressor', label: 'Compressor' },
  { value: 'other', label: 'Other' },
];

const OPERATING_CONDITIONS: { value: OperatingCondition; label: string }[] = [
  { value: 'normal_load', label: 'Normal Load' },
  { value: 'full_load', label: 'Full Load' },
  { value: 'idle', label: 'Idle' },
  { value: 'startup', label: 'Start-up' },
  { value: 'shutdown', label: 'Shutdown' },
  { value: 'unknown', label: 'Unknown' },
];

const ACCEPTED_AUDIO = '.wav,.mp3,audio/wav,audio/mpeg';
const ACCEPTED_VIDEO = '.mp4,.mov,video/mp4,video/quicktime';
const MAX_AUDIO_MB = 50;
const MAX_VIDEO_MB = 500;

// ── Types ──────────────────────────────────────────────────────────────────

type Step = 'asset' | 'context' | 'media' | 'review' | 'processing' | 'done';

interface UploadState {
  file: File | null;
  progress: number;
  error: string | null;
}

const EMPTY_FORM: IntakeFormPayload = {
  asset_name: '',
  asset_type: 'rotating_machinery',
  operating_condition: 'unknown',
};

// ── Field component (defined outside to prevent remount) ───────────────────

function Field({
  label, id, required, hint, error, children,
}: {
  label: string; id: string; required?: boolean; hint?: string;
  error?: string; children: React.ReactNode;
}) {
  return (
    <div className={`field ${error ? 'field--error' : ''}`}>
      <label className="field-label" htmlFor={id}>
        {label}{required && <span className="required" aria-hidden>*</span>}
      </label>
      {hint && <p className="field-hint">{hint}</p>}
      {children}
      {error && <p className="field-error" role="alert">{error}</p>}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export function IntakeForm({ onComplete }: { onComplete: (jobId: string) => void }) {
  const [step, setStep] = useState<Step>('asset');
  const [form, setForm] = useState<IntakeFormPayload>(EMPTY_FORM);
  const [audio, setAudio] = useState<UploadState>({ file: null, progress: 0, error: null });
  const [video, setVideo] = useState<UploadState>({ file: null, progress: 0, error: null });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  const audioRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);

  // ── Field helpers ──────────────────────────────────────────────────────

  const set = useCallback(
    (field: keyof IntakeFormPayload) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const val = e.target.value;
        setForm((f) => ({ ...f, [field]: val === '' ? undefined : val }));
        setErrors((errs) => { const n = { ...errs }; delete n[field]; return n; });
      },
    []
  );

  const setNum = useCallback((field: keyof IntakeFormPayload) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      setForm((f) => ({ ...f, [field]: v === '' ? undefined : Number(v) }));
    }, []);

  // ── Validation ─────────────────────────────────────────────────────────

  function validateAsset(): boolean {
    const errs: Record<string, string> = {};
    if (!form.asset_name?.trim()) errs.asset_name = 'Asset name is required.';
    if (form.asset_type === 'other' && !form.asset_type_custom?.trim())
      errs.asset_type_custom = 'Specify the asset type.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function validateMedia(): boolean {
    if (!audio.file && !video.file) {
      setErrors({ media: 'Upload at least one audio or video file.' });
      return false;
    }
    return true;
  }

  // ── File handlers ──────────────────────────────────────────────────────

  function handleFile(kind: 'audio' | 'video', file: File) {
    const maxMb = kind === 'audio' ? MAX_AUDIO_MB : MAX_VIDEO_MB;
    if (file.size > maxMb * 1024 * 1024) {
      const setter = kind === 'audio' ? setAudio : setVideo;
      setter((s) => ({ ...s, error: `File exceeds ${maxMb} MB limit.` }));
      return;
    }
    const setter = kind === 'audio' ? setAudio : setVideo;
    setter({ file, progress: 0, error: null });
    setErrors((e) => { const n = { ...e }; delete n.media; return n; });
  }

  function onDrop(kind: 'audio' | 'video') {
    return (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) handleFile(kind, file);
    };
  }

  // ── Submission ─────────────────────────────────────────────────────────

  async function handleSubmit() {
    setStep('processing');
    setSubmitError(null);
    try {
      const { job_id, upload_urls } = await submitIntake(form);
      setJobId(job_id);
      if (audio.file && upload_urls.audio) {
        await uploadMedia(upload_urls.audio, audio.file, (pct) =>
          setAudio((s) => ({ ...s, progress: pct }))
        );
      }
      if (video.file && upload_urls.video) {
        await uploadMedia(upload_urls.video, video.file, (pct) =>
          setVideo((s) => ({ ...s, progress: pct }))
        );
      }
      await triggerAnalysis(job_id);
      setStep('done');
      onComplete(job_id);
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Submission failed. Please try again.');
      setStep('review');
    }
  }

  // ── Step indicator ─────────────────────────────────────────────────────

  const STEPS: Step[] = ['asset', 'context', 'media', 'review'];
  const stepIdx = STEPS.indexOf(step as Step);
  const labels = ['Asset', 'Context', 'Media', 'Review'];

  const stepIndicator = (
    <div className="step-indicator" aria-label="Progress">
      {labels.map((label, i) => (
        <div
          key={label}
          className={`step-dot ${i < stepIdx ? 'complete' : i === stepIdx ? 'active' : ''}`}
        >
          <span className="step-number">{i < stepIdx ? '✓' : i + 1}</span>
          <span className="step-label">{label}</span>
        </div>
      ))}
      <div className="step-track">
        <div className="step-fill" style={{ width: `${(stepIdx / (labels.length - 1)) * 100}%` }} />
      </div>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="intake-form">
      <header className="intake-header">
        <div className="wordmark">AxiomSensa</div>
        <p className="wordmark-sub">Fault Diagnosis · Kairos Ventures</p>
      </header>

      {step !== 'processing' && step !== 'done' && stepIndicator}

      <div className="form-body">

        {/* ── Step 1: Asset ── */}
        {step === 'asset' && (
          <div className="form-step">
            <h2 className="step-title">Asset identification</h2>
            <p className="step-desc">Tell us what we're diagnosing.</p>

            <Field label="Asset name" id="asset_name" required error={errors.asset_name}>
              <input
                id="asset_name" type="text" className="input"
                placeholder="e.g. Compressor Unit 3A"
                value={form.asset_name}
                onChange={set('asset_name')}
              />
            </Field>

            <Field label="Asset ID / tag" id="asset_id" hint="Internal plant tag, if any.">
              <input
                id="asset_id" type="text" className="input"
                placeholder="e.g. PLT-C3-002"
                value={form.asset_id ?? ''}
                onChange={set('asset_id')}
              />
            </Field>

            <Field label="Asset type" id="asset_type" required>
              <select id="asset_type" className="input"
                value={form.asset_type} onChange={set('asset_type')}>
                {ASSET_TYPES.map((a) => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
            </Field>

            {form.asset_type === 'other' && (
              <Field label="Describe asset type" id="asset_type_custom"
                required error={errors.asset_type_custom}>
                <input id="asset_type_custom" type="text" className="input"
                  placeholder="e.g. Centrifuge"
                  value={form.asset_type_custom ?? ''}
                  onChange={set('asset_type_custom')} />
              </Field>
            )}

            <div className="field-row">
              <Field label="Manufacturer" id="manufacturer">
                <input id="manufacturer" type="text" className="input"
                  placeholder="e.g. Siemens"
                  value={form.manufacturer ?? ''}
                  onChange={set('manufacturer')} />
              </Field>
              <Field label="Model number" id="model_number">
                <input id="model_number" type="text" className="input"
                  placeholder="e.g. 1LE1001"
                  value={form.model_number ?? ''}
                  onChange={set('model_number')} />
              </Field>
            </div>

            <div className="field-row">
              <Field label="Year installed" id="year_installed">
                <input id="year_installed" type="number" className="input"
                  placeholder="e.g. 2018" min={1900} max={2100}
                  value={form.year_installed ?? ''}
                  onChange={setNum('year_installed')} />
              </Field>
              <Field label="Location" id="location">
                <input id="location" type="text" className="input"
                  placeholder="e.g. Building B, Level 2"
                  value={form.location ?? ''}
                  onChange={set('location')} />
              </Field>
            </div>

            <div className="form-actions">
              <button className="btn btn--primary"
                onClick={() => validateAsset() && setStep('context')}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Context ── */}
        {step === 'context' && (
          <div className="form-step">
            <h2 className="step-title">Operating context</h2>
            <p className="step-desc">More context = more accurate diagnosis.</p>

            <Field label="Operating condition at time of capture" id="operating_condition" required>
              <select id="operating_condition" className="input"
                value={form.operating_condition} onChange={set('operating_condition')}>
                {OPERATING_CONDITIONS.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </Field>

            <Field label="Hours since last maintenance" id="hours_since_last_maintenance"
              hint="Leave blank if unknown.">
              <input id="hours_since_last_maintenance" type="number" className="input"
                placeholder="e.g. 720" min={0} step={0.5}
                value={form.hours_since_last_maintenance ?? ''}
                onChange={setNum('hours_since_last_maintenance')} />
            </Field>

            <Field label="Symptoms observed" id="symptoms_observed"
              hint="Unusual sounds, vibrations, heat, leaks, etc.">
              <textarea id="symptoms_observed" className="input textarea" rows={4}
                placeholder="e.g. High-pitched intermittent squeal above 3,000 RPM."
                value={form.symptoms_observed ?? ''}
                onChange={set('symptoms_observed')} />
            </Field>

            <Field label="Known issues / maintenance history" id="known_issues">
              <textarea id="known_issues" className="input textarea" rows={3}
                placeholder="e.g. Bearing replaced 6 months ago."
                value={form.known_issues ?? ''}
                onChange={set('known_issues')} />
            </Field>

            <div className="field-row">
              <Field label="Captured by" id="captured_by">
                <input id="captured_by" type="text" className="input"
                  placeholder="Technician name / ID"
                  value={form.captured_by ?? ''}
                  onChange={set('captured_by')} />
              </Field>
              <Field label="Site reference" id="site_ref">
                <input id="site_ref" type="text" className="input"
                  placeholder="e.g. WO-20241108"
                  value={form.site_ref ?? ''}
                  onChange={set('site_ref')} />
              </Field>
            </div>

            <Field label="Urgency note" id="urgency_note"
              hint="Any deadline or safety concern.">
              <input id="urgency_note" type="text" className="input"
                placeholder="e.g. Production stop scheduled in 48 h"
                value={form.urgency_note ?? ''}
                onChange={set('urgency_note')} />
            </Field>

            <div className="form-actions">
              <button className="btn btn--ghost" onClick={() => setStep('asset')}>← Back</button>
              <button className="btn btn--primary" onClick={() => setStep('media')}>Continue →</button>
            </div>
          </div>
        )}

        {/* ── Step 3: Media ── */}
        {step === 'media' && (
          <div className="form-step">
            <h2 className="step-title">Upload recordings</h2>
            <p className="step-desc">Upload audio, video, or both. At least one file is required.</p>

            {/* Audio dropzone */}
            <label
              className={`dropzone ${audio.file ? 'dropzone--filled' : ''} ${audio.error ? 'dropzone--error' : ''}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop('audio')}
              htmlFor="audio-input"
            >
              <input id="audio-input" ref={audioRef} type="file" accept={ACCEPTED_AUDIO}
                className="sr-only"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile('audio', f); }} />
              <div className="dropzone-icon">🎙</div>
              <div className="dropzone-text">
                {audio.file ? (
                  <><strong>{audio.file.name}</strong><span>{(audio.file.size / 1024 / 1024).toFixed(1)} MB</span></>
                ) : (
                  <><strong>Audio recording</strong><span>WAV or MP3 — up to 50 MB</span><span className="dropzone-cta">Drop file here or click to browse</span></>
                )}
              </div>
              {audio.file && (
                <button className="dropzone-remove" aria-label="Remove audio"
                  onClick={(e) => { e.preventDefault(); setAudio({ file: null, progress: 0, error: null }); if (audioRef.current) audioRef.current.value = ''; }}>✕</button>
              )}
              {audio.error && <p className="dropzone-error">{audio.error}</p>}
            </label>

            {/* Video dropzone */}
            <label
              className={`dropzone ${video.file ? 'dropzone--filled' : ''} ${video.error ? 'dropzone--error' : ''}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop('video')}
              htmlFor="video-input"
            >
              <input id="video-input" ref={videoRef} type="file" accept={ACCEPTED_VIDEO}
                className="sr-only"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile('video', f); }} />
              <div className="dropzone-icon">🎥</div>
              <div className="dropzone-text">
                {video.file ? (
                  <><strong>{video.file.name}</strong><span>{(video.file.size / 1024 / 1024).toFixed(1)} MB</span></>
                ) : (
                  <><strong>Video recording</strong><span>MP4 or MOV — up to 50 MB</span><span className="dropzone-cta">Drop file here or click to browse</span></>
                )}
              </div>
              {video.file && (
                <button className="dropzone-remove" aria-label="Remove video"
                  onClick={(e) => { e.preventDefault(); setVideo({ file: null, progress: 0, error: null }); if (videoRef.current) videoRef.current.value = ''; }}>✕</button>
              )}
              {video.error && <p className="dropzone-error">{video.error}</p>}
            </label>

            {errors.media && (
              <p className="field-error field-error--standalone" role="alert">{errors.media}</p>
            )}

            <div className="form-actions">
              <button className="btn btn--ghost" onClick={() => setStep('context')}>← Back</button>
              <button className="btn btn--primary"
                onClick={() => validateMedia() && setStep('review')}>Review →</button>
            </div>
          </div>
        )}

        {/* ── Step 4: Review ── */}
        {step === 'review' && (
          <div className="form-step">
            <h2 className="step-title">Confirm & submit</h2>
            <p className="step-desc">Review before starting the diagnosis.</p>

            <dl className="review-list">
              {(
                [
                  ['Asset name', form.asset_name],
                  ['Asset type', form.asset_type === 'other' ? form.asset_type_custom : form.asset_type],
                  ['Asset ID', form.asset_id],
                  ['Manufacturer', form.manufacturer],
                  ['Model', form.model_number],
                  ['Location', form.location],
                  ['Year installed', form.year_installed],
                  ['Operating condition', form.operating_condition],
                  ['Hours since maintenance', form.hours_since_last_maintenance],
                  ['Captured by', form.captured_by],
                  ['Site ref', form.site_ref],
                  ['Audio file', audio.file?.name],
                  ['Video file', video.file?.name],
                ] as [string, string | number | undefined][]
              )
                .filter(([, v]) => v !== undefined && v !== '')
                .map(([label, value]) => (
                  <div className="review-row" key={label}>
                    <dt>{label}</dt>
                    <dd>{String(value)}</dd>
                  </div>
                ))}
            </dl>

            {form.symptoms_observed && (
              <div className="review-block">
                <h4>Symptoms observed</h4>
                <p>{form.symptoms_observed}</p>
              </div>
            )}
            {form.known_issues && (
              <div className="review-block">
                <h4>Known issues</h4>
                <p>{form.known_issues}</p>
              </div>
            )}

            {submitError && (
              <div className="alert alert--error" role="alert">
                <strong>Submission error:</strong> {submitError}
              </div>
            )}

            <div className="form-actions">
              <button className="btn btn--ghost" onClick={() => setStep('media')}>← Back</button>
              <button className="btn btn--primary" onClick={handleSubmit}>Run diagnosis →</button>
            </div>
          </div>
        )}

        {/* ── Processing ── */}
        {step === 'processing' && (
          <div className="form-step form-step--processing">
            <div className="spinner" aria-label="Processing" />
            <h2 className="step-title">Uploading &amp; queuing analysis</h2>
            {audio.file && (
              <div className="progress-row">
                <span>🎙 Audio</span>
                <div className="progress-bar"><div style={{ width: `${audio.progress}%` }} /></div>
                <span>{audio.progress}%</span>
              </div>
            )}
            {video.file && (
              <div className="progress-row">
                <span>🎥 Video</span>
                <div className="progress-bar"><div style={{ width: `${video.progress}%` }} /></div>
                <span>{video.progress}%</span>
              </div>
            )}
            <p className="processing-note">Do not close this tab until upload completes.</p>
          </div>
        )}

        {/* ── Done ── */}
        {step === 'done' && (
          <div className="form-step form-step--done">
            <div className="done-icon">⚡</div>
            <h2 className="step-title">Analysis in progress</h2>
            <p>Job ID: <code>{jobId}</code></p>
            <p className="step-desc">
              AxiomSensa is running optical flow analysis and Claude vision on your recordings.
              This typically takes 1–3 minutes.
            </p>
            <button className="btn btn--primary" onClick={() => jobId && onComplete(jobId)}>
              View report →
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
